import { useSelector } from 'react-redux'
import { usePreviewImageAlt } from '~/app/features/Gallery/hooks/usePreviewImageAlt'
import { getH1Title, getLocaleSelector, getIgpLevel } from '~/app/store/config/reducer'
import { getExperienceType } from '~/app/store/debug/reducer'
import { getColorSwatchColors, getIsColorSwatchesSimilarColors } from '~/app/utils/getColorSwatchColors'
import { hash } from '~/app/utils/hash'
import { EXPERIENCE_TYPE } from '~/shared/constants'
import { useI18nColorName } from '~/app/features/Gallery/hooks/useI18nColorName'
import { getFormattedH1Title } from 'app/utils/getFormattedH1Title'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { State } from 'shared/types/gallery/state'

const getTemplateCategory = (id: string, categoryNamesArray: string[]): string => {
  const hashId = hash(id)
  const index = Number(hashId) % categoryNamesArray.length

  return categoryNamesArray[index]
}

export const useNewTemplateAltText = (tileEntity: State.TileEntity, colorSwatchObjects: GalleryContentQuery.ColorSwatch[]): string => {
  const defaultAltText = usePreviewImageAlt(tileEntity.comparativeName)
  const igpLevel = useSelector(getIgpLevel)
  const experienceType = useSelector(getExperienceType)
  const h1Title = useSelector(getH1Title)
  const locale = useSelector(getLocaleSelector)
  const isDefaultLocale = locale === 'en-us'
  const isL0OrGalleryPage = igpLevel === 0 || experienceType === EXPERIENCE_TYPE.GALLERY
  const shouldUseNewAltText = isDefaultLocale && h1Title && tileEntity.categoryNames && tileEntity.categoryNames.length

  const isColorSwatchColorsSimilar = getIsColorSwatchesSimilarColors(colorSwatchObjects)

  const { primaryColor, secondaryColor } = getColorSwatchColors(tileEntity.colorComposition, isColorSwatchColorsSimilar)

  const primaryColorTitle = useI18nColorName(primaryColor, locale)
  const secondaryColorTitle = useI18nColorName(secondaryColor, locale)

  if (!shouldUseNewAltText) {
    return tileEntity.altText ?? defaultAltText
  }

  const formattedH1Title = getFormattedH1Title(h1Title)

  const formattedCategory = getTemplateCategory(tileEntity.designId, tileEntity.categoryNames).toLocaleLowerCase()

  if (!tileEntity.colorComposition.length) {
    return `${formattedH1Title} for ${formattedCategory}`
  }

  if (!secondaryColor) {
    return isL0OrGalleryPage ? `${formattedH1Title} in ${primaryColorTitle} for ${formattedCategory}` : `${formattedH1Title} in ${primaryColorTitle}`
  }

  return isL0OrGalleryPage
    ? `${formattedH1Title} in ${primaryColorTitle} and ${secondaryColorTitle} for ${formattedCategory}`
    : `${formattedH1Title} in ${primaryColorTitle} and ${secondaryColorTitle}`
}
